import { Loader2, type LucideIcon, Shield, ShieldAlert, ShieldCheck, ShieldX } from 'lucide-react';

/**
 * Visual configuration for a single collision simulation state.
 *
 * Shared source of truth so the project-detail panel and the compact
 * list/table status tag stay visually consistent. `icon` is a lucide
 * component reference so each consumer can size/style it as needed
 * (capitalize at the JSX call site, e.g. `const Icon = cfg.icon`).
 */
export interface CollisionStateConfig {
  /** Lucide icon component for this state (capitalize at the JSX call site). */
  icon: LucideIcon;
  /** Icon text color class (e.g. for the detail panel's larger icon). */
  iconText: string;
  /** Gradient accent strip class (detail panel top border). */
  accentClass: string;
  /** Icon circle background class (detail panel). */
  iconBg: string;
  /** Status dot background class (detail panel). */
  dot: string;
  /** Status text color class (detail panel). */
  text: string;
  /** Compact tag background class (list card / table row). */
  tagBg: string;
  /** Compact tag text color class (list card / table row). */
  tagText: string;
}

/** Known collision simulation states mapped to their visual config. */
export const COLLISION_STATE_CONFIG = {
  running: {
    icon: Loader2,
    iconText: 'text-blue-600',
    accentClass: 'from-blue-400 to-indigo-500',
    iconBg: 'bg-blue-100',
    dot: 'bg-blue-500 animate-pulse',
    text: 'text-blue-700',
    tagBg: 'bg-blue-100',
    tagText: 'text-blue-700',
  },
  // biome-ignore lint/style/useNamingConvention: keys mirror backend collision_simulation_state values + i18n keys
  complete_ok: {
    icon: ShieldCheck,
    iconText: 'text-emerald-600',
    accentClass: 'from-emerald-400 to-green-500',
    iconBg: 'bg-emerald-100',
    dot: 'bg-emerald-500',
    text: 'text-emerald-700',
    tagBg: 'bg-emerald-100',
    tagText: 'text-emerald-700',
  },
  // biome-ignore lint/style/useNamingConvention: keys mirror backend collision_simulation_state values + i18n keys
  complete_collisions: {
    icon: ShieldAlert,
    iconText: 'text-amber-600',
    accentClass: 'from-amber-400 to-orange-500',
    iconBg: 'bg-amber-100',
    dot: 'bg-amber-500',
    text: 'text-amber-700',
    tagBg: 'bg-amber-100',
    tagText: 'text-amber-700',
  },
  error: {
    icon: ShieldX,
    iconText: 'text-red-600',
    accentClass: 'from-red-400 to-rose-500',
    iconBg: 'bg-red-100',
    dot: 'bg-red-500',
    text: 'text-red-700',
    tagBg: 'bg-red-100',
    tagText: 'text-red-700',
  },
} as const satisfies Record<string, CollisionStateConfig>;

/** Fallback config for unknown / unrecognized states. */
export const COLLISION_FALLBACK_CONFIG: CollisionStateConfig = {
  icon: Shield,
  iconText: 'text-gray-500',
  accentClass: 'from-gray-300 to-gray-400',
  iconBg: 'bg-gray-100',
  dot: 'bg-gray-400',
  text: 'text-gray-600',
  tagBg: 'bg-gray-100',
  tagText: 'text-gray-600',
};

export type CollisionState = keyof typeof COLLISION_STATE_CONFIG;

/** Returns the visual config for a state, falling back when unknown. */
export const getCollisionStateConfig = (state: string | null | undefined): CollisionStateConfig =>
  state && state in COLLISION_STATE_CONFIG
    ? COLLISION_STATE_CONFIG[state as CollisionState]
    : COLLISION_FALLBACK_CONFIG;

/** Returns true when the state is one of the known collision states. */
export const isKnownCollisionState = (state: string | null | undefined): state is CollisionState =>
  !!state && state in COLLISION_STATE_CONFIG;
